// import { FaceDetector, FilesetResolver } from "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0";

import {FaceDetector, FilesetResolver} from "@mediapipe/tasks-vision";
import {reactive} from "vue";

import store from "@/store";
import {NoDevicePermissionError} from "@/errors/NoDevicePermissionError"
import {CameraPermissionDeniedError} from "@/errors/CameraPermissionDeniedError";
import constant from "@/constant";


//ref: https://developers.google.com/edge/mediapipe/solutions/vision/face_detector
class CmFaceDetector {


    /**
     * @param {HTMLVideoElement} videoElement - The video element displaying the webcam stream.
     * @param {HTMLElement} liveViewElement - The element where detections will be drawn.
     */
    constructor(videoRef, livewViewRef) {

        this.video = videoRef;
        // store.dispatch('Monitoring/setFaceDetectionViewId', videoElementId);
        this.liveView = livewViewRef;
        this.stream = null;
        this.faceDetector = null;
        this.lastVideoTime = -1;
        this.animationFrameId = null; // To store the requestAnimationFrame ID for cancellation
        this.children = []; // To store references to drawn elements for cleanup
        this.handler = this.displayVideoDetections


        // Bind event handlers to the class instance
        this.predictWebcam = this.predictWebcam.bind(this);
        this.handleLoadedData = this.handleLoadedData.bind(this);

        this.state = reactive({
            count: 0,
            results: [],
        })
        this.liveViewWidth = constant.VIDEO_FEED_DEFAULT_VIDEO_HEIGHT;
        this.liveViewHeight = constant.VIDEO_FEED_DEFAULT_VIDEO_HEIGHT;

        this.constraints = {
            audio: false,
            video: {
                deviceId: {exact: null},
                width: constant.VIDEO_FEED_DEFAULT_VIDEO_HEIGHT,
                height: constant.VIDEO_FEED_DEFAULT_VIDEO_HEIGHT,
                facingMode: "user",
                frameRate: {max: 30},

            },
        };

        this.cameraPermissionStatus = null;


    }


    async setCameraPermissionState() {
        // listen camera permission status
        await navigator.permissions.query({name: 'camera'}).then((cameraPermissionStatus) => {

            if (cameraPermissionStatus.state == 'denied') {
                throw new CameraPermissionDeniedError();
            }
            this.cameraPermissionStatus = cameraPermissionStatus;


            this.cameraPermissionStatus.addEventListener('change', () => {

                const currentState = cameraPermissionStatus.state;
                store.dispatch('Monitoring/setCameraPermissionStatus', currentState);
            })


        });
    }


    /**
     * Initializes the MediaPipe FaceDetector.
     */
    async __initializeFaceDetector() {
        try {

            const vision = await FilesetResolver.forVisionTasks(
                "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
            );

            const model_path = import.meta.env.BASE_URL + 'static/lib/face_detection_models/blaze_face_short_range.tflite';

            this.faceDetector = await FaceDetector.createFromOptions(vision, {
                baseOptions: {
                    modelAssetPath: model_path,
                    // modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite`,
                    delegate: "GPU"
                },
                runningMode: "VIDEO"
            });


        } catch (error) {

            alert("Failed to load face detection model. Please check your internet connection.");
        }
    }

    /**
     * Checks if webcam access is supported by the browser.
     * @returns {boolean}
     */

    async playVideo(videoDeviceID) {

        this.constraints.video.deviceId.exact = videoDeviceID;
        // console.log("Attempting getUserMedia with device ID:", videoDeviceID); // Log the new ID


        try {
            let stream = await navigator.mediaDevices.getUserMedia(this.constraints);


            // const actualDeviceId = stream.getVideoTracks()[0].getSettings().deviceId;
            // console.log("actual connected device ID:", actualDeviceId); // Log the new ID


            this.video.value.srcObject = stream;


            this.video.value.onloadedmetadata = () => {
                // Fix for iOS Safari from https://leemartin.dev/hello-webrtc-on-safari-11-e8bcb5335295
                this.video.value.setAttribute('autoplay', '');
                this.video.value.setAttribute('muted', '');
                // this.video.value.playsinline=true;


                this.video.value.play().then(() => {
                    console.debug('video start playing')
                }).catch((error) => {
                    console.log(error);
                })
            };

            this.video.value.addEventListener("loadeddata", this.handleLoadedData);

        } catch (err) {
            console.log(err);
            throw new NoDevicePermissionError();
            // always check for errors at the end.
            // need to show error message that you need to setup camera
        }
    }

    /**
     * Starts the face detection process: initializes the detector and enables the webcam.
     */
    async start() {

        await this.__initializeFaceDetector();

        if (!this.faceDetector) {
            alert("Face Detector is still loading. Please try again.");
            return;
        }

        if (this.hasGetUserMedia()) {
            let videoDeviceId = store.getters['Monitoring/cameraId'];
            await this.playVideo(videoDeviceId)

        } else {
            console.debug('not media')
        }

        navigator.mediaDevices.ondevicechange = (event) => {
            // detect camera list device changes
            this.setCameraList();
        };
    }

    setLiveViewDimension(width, height) {
        this.liveViewWidth = width
        this.liveViewHeight = height
    }

    async setCameraList() {

        await navigator.mediaDevices.enumerateDevices().then((devices) => {

            let videoDeviceList = [];
            devices.forEach((device) => {

                if (device.kind == 'videoinput') {
                    if (device.deviceId === '') {
                        throw new NoDevicePermissionError();
                    }

                    let detected = false;
                    videoDeviceList.forEach((savedDevice) => {

                        if (savedDevice.deviceId === device.deviceId) {
                            detected = true;
                        }
                    })
                    if (!detected) {
                        videoDeviceList.push(device);
                    }
                }
            })

            videoDeviceList.sort((a, b) => {
                const aLabel = a.label.toLowerCase();
                const bLabel = b.label.toLowerCase();
                if (aLabel.includes('front') || aLabel.includes('user')) return -1;
                if (bLabel.includes('front') || bLabel.includes('user')) return 1;
                return 0; // Maintain original order if no specific keywords
            });

            store.dispatch('Monitoring/setCameraDevices', videoDeviceList)
        })

    }

    clearFrame() {
        const ctx = this.liveView.value.getContext('2d');
        ctx.save();
        ctx.clearRect(0, 0, this.liveView.value.width, this.liveView.value.height);
        ctx.restore();
    }


    drawframe() {
        const ctx = this.liveView.value.getContext('2d');
        const video = this.video.value;

        if (!ctx || !video || video.videoWidth === 0) { // Add a check to ensure context and video exist
            console.warn("Canvas context or video element not available for drawing.");
            return;
        }


        this.liveView.value.width = this.liveViewWidth;
        this.liveView.value.height = this.liveViewHeight;


        // calculate the crop coordinates to center the video
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const cropSize = Math.min(videoWidth, videoHeight); // determine the smaller dimension : 480px

        const xOffset = (videoWidth - cropSize) / 2; // (640 - 480) /2 = 80
        const yOffset = (videoHeight - cropSize) / 2; // (480 - 480) /2 = 0

        ctx.clearRect(0, 0, this.liveView.value.width, this.liveView.value.height);

        // Save the current canvas state before transformations
        ctx.save();

        // Apply transformations for mirroring if desired
        ctx.translate(this.liveView.value.width, 0); // Move origin to the right edge
        ctx.scale(-1, 1); // Flip horizontally

        // Draw the video frame from the video element onto the canvas

        ctx.drawImage(this.video.value,
            xOffset, yOffset, cropSize, cropSize, // Source(video) coordinates
            0, 0, this.liveView.value.width, this.liveView.value.height); //Canvas cordinates

        // Restore the canvas state to undo transformations for subsequent drawings (like bounding boxes)
        ctx.restore();
    }


    hasGetUserMedia() {
        return !!navigator.mediaDevices?.getUserMedia;
    }

    /**
     * Handler for when the video data is loaded.
     */
    handleLoadedData() {
        console.debug("Webcam video data loaded. Starting prediction...");

        /**
         * When browser has no permission to access camera, handleLoadedData is not called.
         * Then the browser grant the permission, this function is called, but deviceId is set to null
         * We need to set Camera list again to ensure that users can see the device lists.
         */
        if (store.state.Monitoring.deviceId == null) {
            this.setCameraList();
        }

        this.video.value.removeEventListener("loadeddata", this.handleLoadedData); // Remove listener to prevent multiple calls

        this.predictWebcam(this.displayVideoDetections);

    }

    /**
     * Continuously predicts faces in the webcam stream.
     */
    async predictWebcam() {
        this.drawframe()
        const startTimeMs = performance.now();

        // Only run detection if the video frame has changed
        if (this.video.currentTime !== this.lastVideoTime) {
            this.lastVideoTime = this.video.value.currentTime;
            if (this.faceDetector) {
                const detections = this.faceDetector.detectForVideo(this.video.value, startTimeMs).detections;
                this.handler(detections);
            }
        }

        this.animationFrameId = window.requestAnimationFrame(this.predictWebcam);
    }


    _initState() {
        this.state = {
            count: 0,
            results: [],

        }
    }


    /**
     * Displays the detected faces on the live view.
     * @param {Array} detections - An array of face detection results.
     */
    displayVideoDetections(detections) {
        this._initState();

        // Remove any highlighting from previous frame.
        for (const child of this.children) {
            this.liveView.value.removeChild(child);
        }
        this.children.length = 0; // Clear the array
        this.state.count = detections.length;

        let index = 0;

        for (const detection of detections) {

            const {boundingBox, categories, keypoints} = detection;
            const confidence = Math.round(parseFloat(categories[0].score) * 100);


            // Adjust positions for flipped video (assuming default webcam mirror)
            // This is often needed when the video element itself is mirrored with CSS transform: scaleX(-1);
            const videoWidth = this.video.value.offsetWidth;
            const videoHeight = this.video.value.offsetHeight;

            let ratio = videoWidth / 583;
            let adjustedOriginX = videoWidth - boundingBox.width * ratio - boundingBox.originX * ratio;


            // show confidence text
            const p = document.createElement("p");
            p.className = "confidencePoint";
            p.innerText = `Confidence: ${confidence}%.`;
            p.style.cssText = `left: ${adjustedOriginX + 30 * ratio}px;
                               top: ${boundingBox.originY * ratio - 30}px;
                               width: ${boundingBox.width - 10}px;`;


            // green area that shows where the face is
            let x = adjustedOriginX + 30 * ratio;
            let y = boundingBox.originY * ratio;
            let width = boundingBox.width * ratio;
            let height = boundingBox.height * ratio;

            const highlighter = document.createElement("div");
            highlighter.setAttribute("class", "highlighter");
            highlighter.style.cssText = `
                        left: ${x}px ;
                        top: ${y}px;
                        width: ${width}px;
                        height: ${height}px;`;

            this.liveView.value.appendChild(highlighter);
            this.liveView.value.appendChild(p);
            this.children.push(highlighter, p);

            this.state.results[index] = {
                confidence: confidence,
                detected_area: {
                    x: x,
                    y: y,
                    width: width,
                    height: height,
                }
            }

            if (keypoints) {
                for (const keypoint of keypoints) {
                    const keypointEl = document.createElement("span"); // span is more semantic than spam
                    keypointEl.className = "key-point";
                    // Keypoints are normalized [0,1], so multiply by video dimensions
                    const adjustedKeypointX = videoWidth - (keypoint.x * videoWidth); // Adjust for mirrored video
                    const adjustedKeypointY = keypoint.y * videoHeight;

                    keypointEl.style.cssText = `top: ${adjustedKeypointY - 3}px; left: ${adjustedKeypointX - 3}px;`;
                    this.liveView.value.appendChild(keypointEl);
                    this.children.push(keypointEl);
                }
            }

            index++;
        }
        store.dispatch('Monitoring/setFaceDetectionState', this.state)
    }


    /**
     * Stops the webcam stream and clears all detections.
     */
    async stop(closeVideo = false) {
        console.debug('stop camera');

        if (this.animationFrameId) {
            window.cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        if (closeVideo && this.video.value.srcObject) {
            const stream = this.video.value.srcObject;
            stream.getTracks().forEach(track => {
                // console.log("Stopping track:", track.label, "ID:", track.getSettings().deviceId);
                track.stop()
            });
            this.video.value.srcObject = null;
        }

        for (const child of this.children) {

            this.liveView.value.removeChild(child);
        }

        this.children.length = 0;
        this.lastVideoTime = -1; // Reset for next start
        store.dispatch('Monitoring/setCameraDisconnected');
        console.debug("Face detection stopped and resources cleared.");

    }


}


export {CmFaceDetector};